import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useAuth } from '../../../../../shared/context/useAuth';
import usePatientProfile from '../../../hooks/UsePatientProfile';
import { productService } from '../../../../../services/productService';
import { paymentService } from '../../../../../services/paymentService';

export default function Checkout() {
  const { patient } = usePatientProfile();
  const { session } = useAuth();
  const [activeTab, setActiveTab] = useState('credits'); // 'credits' ou 'packages'
  const [packageType, setPackageType] = useState('individual'); // 'individual', 'casal', 'familia'
  const [creditType, setCreditType] = useState('individual'); // Definir um valor padrão
  
  // Buscar produtos usando React Query com refetch interval para manter dados atualizados
  const {
    data: productsData,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['products'],
    queryFn: productService.getProducts,
    refetchOnWindowFocus: true,
    staleTime: 5 * 60 * 1000, // 5 minutos
    retry: 3
  });

  // Formatar preço para exibição
const formatPrice = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount / 100);
};

  // Iniciar checkout no Stripe com informações adicionais sobre o tipo
 const handleCheckout = async (priceId, isPackage = false, type = 'individual') => {
  try {
    // Validar dados antes de prosseguir
    if (!patient || !patient[0]?.id) {
      throw new Error('Dados do paciente não encontrados');
    }
    
    if (!session?.user?.email) {
      throw new Error('Dados de usuário não encontrados');
    }
    
    // Log para depuração
    console.log(`Iniciando checkout para ${isPackage ? 'pacote' : 'crédito individual'} do tipo: ${type}`);
    console.log('priceId:', priceId);
    console.log('isPackage:', isPackage);
    console.log('type:', type);
    
    // Preparar metadados de forma estruturada
    const metadata = {
      patientId: patient[0]?.id,
      creditType: type,
      isPackage: isPackage ? 'true' : 'false', // Garantir string para evitar problemas de conversão
    };
    
    // Adicionar metadados específicos para pacotes
    if (isPackage) {
      const packageInfo = getPackageInfo(priceId);
      if (!packageInfo) {
        throw new Error('Informações do pacote não encontradas');
      }
      
      metadata.packageInfo = JSON.stringify({
        sessions: packageInfo.sessions,
        validity: packageInfo.validity,
        sessionValidity: packageInfo.sessionValidity || 7
      });
    }
    
    // Criar payload para checkout
    const checkoutPayload = {
      priceId,
      user: { 
        name: patient[0]?.name, 
        email: session?.user?.email 
      },
      isPackage,
      sessionType: type,
      metadata
    };
    
    // Log para depuração
    console.log('Payload de checkout:', checkoutPayload);
    
    // Chamar API de checkout
    const response = await paymentService.createCheckoutSession(checkoutPayload);
    
    if (!response || !response.url) {
      throw new Error('Resposta de checkout inválida');
    }
    
    // Redirecionar para página de checkout do Stripe
    window.location.href = response.url;
  } catch (error) {
    console.error('Erro ao iniciar checkout:', error);
    // Mostrar erro para o usuário
    alert(`Erro ao iniciar checkout: ${error.message || 'Ocorreu um erro desconhecido'}`);
  }
};
  
  // Função para obter informações do pacote pelo priceId - com validação melhorada
  const getPackageInfo = (priceId) => {
    if (!priceId) return null;
    
    // Obter todos os pacotes disponíveis
    const allPackages = [];
    
    // Verificar se os dados organizados estão disponíveis
    if (organizedPackages) {
      Object.keys(organizedPackages).forEach(type => {
        Object.keys(organizedPackages[type]).forEach(duration => {
          allPackages.push(...organizedPackages[type][duration]);
        });
      });
    }
    
    // Encontrar o pacote pelo ID
    const packageData = allPackages.find(pkg => pkg.id === priceId);
    
    if (!packageData || !packageData.product) {
      console.error('Pacote não encontrado ou inválido:', priceId);
      return null;
    }
    
    // Extrair e validar metadados
    const metadata = packageData.product.metadata || {};
    
    return {
      sessions: parseInt(metadata.sessions || 0, 10),
      validity: parseInt(metadata.validity || 30, 10),
      sessionValidity: parseInt(metadata.sessionValidity || 7, 10)
    };
  };

  // Verificar se os dados foram carregados
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3BBED0]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-6 text-red-500">
        <p>Erro ao carregar produtos. Por favor, tente novamente mais tarde.</p>
        <p className="text-sm mt-2">{error.message}</p>
        <button 
          onClick={() => refetch()} 
          className="mt-4 px-4 py-2 bg-[#3BBED0] text-white rounded hover:bg-[#2AA9BD]"
        >
          Tentar Novamente
        </button>
      </div>
    );
  }

  // Processamento e validação de dados melhorados
  let regularProducts = [];
  let packageProducts = [];

  // Verificar estrutura de dados e processar de acordo
  if (productsData) {
    if (productsData.regular && productsData.packages) {
      // Formato estruturado
      regularProducts = Array.isArray(productsData.regular) ? productsData.regular : [];
      packageProducts = Array.isArray(productsData.packages) ? productsData.packages : [];
    } else if (Array.isArray(productsData)) {
      // Formato de array simples - separar por metadados
      regularProducts = productsData.filter(item => 
        item && item.product && !item.product.metadata?.sessions
      );
      packageProducts = productsData.filter(item => 
        item && item.product && item.product.metadata?.sessions
      );
    } else {
      console.error('Formato de dados não reconhecido:', productsData);
    }
  }

  // Filtrar produtos ativos com validação
  const activeRegularProducts = regularProducts.filter(
    item => item && item.product && item.product.active === true && item.active === true
  );
  
  // Filtrar pacotes ativos com validação
  const activePackageProducts = packageProducts.filter(
    item => item && item.product && item.product.active === true && item.active === true
  );
  
  // Organizar pacotes por tipo e duração - com validação melhorada
  const organizedPackages = {
    individual: {
      "1month": [],
      "3months": [],
      "6months": [],
    },
    casal: {
      "1month": [],
      "3months": [],
      "6months": [],
    },
    familia: {
      "1month": [],
      "3months": [],
      "6months": [],
    },
  };
  
  // Processar e organizar pacotes com validação
  activePackageProducts.forEach(pkg => {
    if (!pkg || !pkg.product || !pkg.product.metadata) return;
    
    const type = pkg.product.metadata.type || 'individual';
    const validity = parseInt(pkg.product.metadata.validity || 30, 10);
    
    // Verificar se o tipo é válido
    if (!organizedPackages[type]) return;
    
    // Determinar a categoria de duração
    let durationCategory;
    if (validity <= 30) {
      durationCategory = "1month";
    } else if (validity <= 90) {
      durationCategory = "3months";
    } else {
      durationCategory = "6months";
    }
    
    // Adicionar à categoria apropriada
    if (organizedPackages[type][durationCategory]) {
      organizedPackages[type][durationCategory].push(pkg);
    }
  });

  // Organizar créditos por tipo com validação melhorada
  const creditsByType = {
    individual: [],
    casal: [],
    familia: []
  };
  
  // Processar e categorizar créditos
  activeRegularProducts.forEach(item => {
    if (!item || !item.product) return;
    
    const product = item.product;
    let type = 'individual'; // Valor padrão
    
    // Determinar o tipo com base em metadados ou nome
    if (product.metadata?.type) {
      type = product.metadata.type;
    } else if (product.unit_label) {
      type = product.unit_label;
    } else if (product.name) {
      const nameLower = product.name.toLowerCase();
      if (nameLower.includes('casal')) {
        type = 'casal';
      } else if (nameLower.includes('família') || nameLower.includes('familia')) {
        type = 'familia';
      }
    }
    
    // Adicionar ao tipo apropriado se existir
    if (creditsByType[type]) {
      creditsByType[type].push(item);
    } else {
      creditsByType.individual.push(item); // Fallback para individual
    }
  });

  // Determinar quais tipos de créditos estão disponíveis
  const availableCreditTypes = Object.keys(creditsByType).filter(
    type => creditsByType[type].length > 0
  );

  // Se não houver tipo específico, adicionar créditos sem tipo específico à categoria "individual"
  if (availableCreditTypes.length === 0 && activeRegularProducts.length > 0) {
    creditsByType.individual = activeRegularProducts;
    availableCreditTypes.push('individual');
  }

  // Definir tipo de crédito padrão se não estiver definido
  if (!creditType && availableCreditTypes.length > 0) {
    setCreditType(availableCreditTypes[0]);
  }

  return (
    <div className="">
      {/* Tabs de navegação principal */}
      <div className="flex border-b mb-6">
        <button
          className={`py-2 px-4 font-medium ${
            activeTab === 'credits'
              ? 'border-b-2 border-[#3BBED0] text-[#3BBED0]'
              : 'text-gray-500'
          }`}
          onClick={() => setActiveTab('credits')}
        >
          Créditos Individuais
        </button>
        <button
          className={`py-2 px-4 font-medium ${
            activeTab === 'packages'
              ? 'border-b-2 border-[#3BBED0] text-[#3BBED0]'
              : 'text-gray-500'
          }`}
          onClick={() => setActiveTab('packages')}
        >
          Pacotes de Terapia
        </button>
      </div>

      {/* Conteúdo baseado na tab ativa */}
      {activeTab === 'credits' ? (
        // Créditos individuais
        <div>
          {/* Tabs de tipo de crédito (se houver mais de um tipo) */}
          {availableCreditTypes.length > 1 && (
            <div className="flex border-b mb-6">
              {availableCreditTypes.map(type => (
                <button
                  key={type}
                  className={`py-2 px-4 font-medium ${
                    creditType === type
                      ? 'border-b-2 border-[#3BBED0] text-[#3BBED0]'
                      : 'text-gray-500'
                  }`}
                  onClick={() => setCreditType(type)}
                >
                  {type === 'individual' ? 'Individual' : 
                   type === 'casal' ? 'Casal' : 'Família'}
                </button>
              ))}
            </div>
          )}
          
          <div className="flex flex-wrap gap-4 mx-auto">
            {creditsByType[creditType]?.length > 0 ? (
              creditsByType[creditType].map((price, index) => (
                <div key={index} className="w-full md:w-[calc(33%-1rem)] p-6 rounded-lg border shadow-sm hover:shadow-md transition-shadow">
                  <div className={`py-1 px-3 rounded-full text-white text-xs font-medium inline-block mb-2 ${
                    creditType === 'individual' ? 'bg-blue-500' : 
                    creditType === 'casal' ? 'bg-purple-500' : 'bg-green-500'
                  }`}>
                    Sessão {creditType === 'individual' ? 'Individual' : 
                            creditType === 'casal' ? 'de Casal' : 'Familiar'}
                  </div>
                  
                  <h2 className="text-lg font-bold mb-2">{price.product.name}</h2>
                  <p className="text-gray-700 mb-4">{price.product.description || `1 sessão de terapia ${creditType}`}</p>
                  <p className="text-4xl font-bold mb-4 text-[#3BBED0]">
                    {formatPrice(price.unit_amount)}
                  </p>
                  <ul className="text-sm text-gray-700 mb-6">
                    <li className="flex items-center mb-2">
                      <span className="text-green-500 mr-2">✓</span> Vídeo conferência
                    </li>
                    <li className="flex items-center mb-2">
                      <span className="text-green-500 mr-2">✓</span> Sala Individual
                    </li>
                    <li className="flex items-center">
                      <span className="text-green-500 mr-2">✓</span> Suporte prioritário
                    </li>
                  </ul>
                  <button
                    onClick={() => handleCheckout(price.id, false, creditType)}
                    className="w-full text-sm desktop:text-[14px] desktop:py-2 desktop:px-5 px-[64px] py-[12px] text-white font-bold p-[24px] bg-[#3BBED0] border-0 rounded-[5px] hover:bg-[#2AA9BD] transition-colors"
                  >
                    Comprar
                  </button>
                </div>
              ))
            ) : (
              <div className="w-full text-center p-8 bg-gray-50 rounded-lg">
                <p className="text-gray-500">Nenhum crédito disponível no momento.</p>
              </div>
            )}
          </div>
        </div>
      ) : (
        // Pacotes de terapia
        <div>
          {/* Tabs de tipo de pacote */}
          <div className="flex border-b mb-6">
            <button
              className={`py-2 px-4 font-medium ${
                packageType === 'individual'
                  ? 'border-b-2 border-[#3BBED0] text-[#3BBED0]'
                  : 'text-gray-500'
              }`}
              onClick={() => setPackageType('individual')}
            >
              Individual
            </button>
            <button
              className={`py-2 px-4 font-medium ${
                packageType === 'casal'
                  ? 'border-b-2 border-[#3BBED0] text-[#3BBED0]'
                  : 'text-gray-500'
              }`}
              onClick={() => setPackageType('casal')}
            >
              Casal
            </button>
            <button
              className={`py-2 px-4 font-medium ${
                packageType === 'familia'
                  ? 'border-b-2 border-[#3BBED0] text-[#3BBED0]'
                  : 'text-gray-500'
              }`}
              onClick={() => setPackageType('familia')}
            >
              Família
            </button>
          </div>
          
          {/* Pacotes de 1 mês */}
          <div className="mb-10">
            <h2 className="text-xl font-bold mb-4 text-gray-800">Plano Mensal</h2>
            <div className="flex flex-wrap gap-4 mx-auto">
              {organizedPackages[packageType]["1month"].length > 0 ? (
                organizedPackages[packageType]["1month"].map((pkg, index) => (
                  <PackageCard 
                    key={index} 
                    packageData={pkg} 
                    onCheckout={() => handleCheckout(pkg.id, true, 4)} 
                    type={packageType}
                  />
                ))
              ) : (
                <div className="w-full text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-gray-500">Nenhum pacote mensal disponível no momento.</p>
                </div>
              )}
            </div>
          </div>
          
          {/* Pacotes de 3 meses */}
          <div className="mb-10">
            <h2 className="text-xl font-bold mb-4 text-gray-800">Plano Trimestral</h2>
            <div className="flex flex-wrap gap-4 mx-auto">
              {organizedPackages[packageType]["3months"].length > 0 ? (
                organizedPackages[packageType]["3months"].map((pkg, index) => (
                  <PackageCard 
                    key={index} 
                    packageData={pkg} 
                    onCheckout={() => handleCheckout(pkg.id, true, packageType)} 
                    type={packageType}
                  />
                ))
              ) : (
                <div className="w-full text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-gray-500">Nenhum pacote trimestral disponível no momento.</p>
                </div>
              )}
            </div>
          </div>
          
          {/* Pacotes de 6 meses */}
          <div>
            <h2 className="text-xl font-bold mb-4 text-gray-800">Plano Semestral</h2>
            <div className="flex flex-wrap gap-4 mx-auto">
              {organizedPackages[packageType]["6months"].length > 0 ? (
                organizedPackages[packageType]["6months"].map((pkg, index) => (
                  <PackageCard 
                    key={index} 
                    packageData={pkg} 
                    onCheckout={() => handleCheckout(pkg.id, true, packageType)} 
                    type={packageType}
                  />
                ))
              ) : (
                <div className="w-full text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-gray-500">Nenhum pacote semestral disponível no momento.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Componente de card para pacotes - com melhorias
function PackageCard({ packageData, onCheckout, type }) {
  // Validação de props
  if (!packageData || !packageData.product) {
    console.error('Dados de pacote inválidos:', packageData);
    return null;
  }
const formatPrice = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount / 100);
};

  // Extrair e validar metadados
  const metadata = packageData.product.metadata || {};
  const validity = parseInt(metadata.validity || 30, 10);
  const sessions = parseInt(metadata.sessions || 4, 10);
  const sessionValidity = parseInt(metadata.sessionValidity || 7, 10);
  
  // Calcular preço por sessão com validação
  const pricePerSession = packageData.unit_amount && sessions 
    ? Math.round(packageData.unit_amount / sessions) 
    : 0;
    
  // Preços de referência para cálculo de desconto
  const originalPricePerSession = type === 'individual' ? 2900 : 
                                 type === 'casal' ? 3500 : 4000;
  
  // Calcular desconto com validação
  const discount = pricePerSession && originalPricePerSession 
    ? Math.round(100 - (pricePerSession * 100 / originalPricePerSession))
    : 0;
  
  // Determinar cor do badge baseado no tipo
  const getBadgeColor = () => {
    switch(type) {
      case 'individual': return 'bg-blue-500';
      case 'casal': return 'bg-purple-500';
      case 'familia': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="w-full md:w-[calc(50%-1rem)] p-6 rounded-lg border shadow-sm hover:shadow-md transition-shadow">
      <div className="bg-[#3BBED0] text-white py-2 px-4 rounded-t-md -mt-6 -mx-6 mb-4 flex justify-between items-center">
        <h2 className="text-lg font-bold">{packageData.product.name}</h2>
        <span className={`${getBadgeColor()} text-white text-xs px-2 py-1 rounded-full`}>
          {type === 'individual' ? 'Individual' : 
           type === 'casal' ? 'Casal' : 'Família'}
        </span>
      </div>
      
      <p className="text-gray-700 mb-4">{packageData.product.description}</p>
      
      <p className="text-4xl font-bold mb-4 text-[#3BBED0]">
        {formatPrice(packageData.unit_amount)}
      </p>
      
      <div className="bg-gray-50 p-3 rounded-md mb-4">
        <p className="text-sm font-medium text-gray-700 flex justify-between">
          <span>{sessions} sessões de terapia</span>
          {discount > 0 && <span className="text-green-600">{discount}% de desconto</span>}
        </p>
        <p className="text-sm text-gray-600">
          Validade do pacote: {validity} dias
        </p>
        <p className="text-sm text-gray-600">
          Prazo para agendar cada sessão: {sessionValidity} dias após ativação
        </p>
        <p className="text-sm text-gray-600 mt-2">
          <span className="font-medium">Valor por sessão:</span> {formatPrice(pricePerSession)}
        </p>
      </div>
      
      <ul className="text-sm text-gray-700 mb-6">
        <li className="flex items-center mb-2">
          <span className="text-green-500 mr-2">✓</span> Vídeo conferência
        </li>
        <li className="flex items-center mb-2">
          <span className="text-green-500 mr-2">✓</span> Sala Individual
        </li>
        <li className="flex items-center mb-2">
          <span className="text-green-500 mr-2">✓</span> Suporte prioritário
        </li>
        <li className="flex items-center">
          <span className="text-green-500 mr-2">✓</span> Economia em relação a sessões avulsas
        </li>
      </ul>
      
      <button
        onClick={onCheckout}
        className="w-full text-sm desktop:text-[14px] desktop:py-2 desktop:px-5 px-[64px] py-[12px] text-white font-bold p-[24px] bg-[#3BBED0] border-0 rounded-[5px] hover:bg-[#2AA9BD] transition-colors"
      >
        Comprar Pacote
      </button>
    </div>
  );
}